import React, { createContext, useReducer } from 'react';
import { merge } from 'lodash';
import { useQuery, UseQueryResult } from 'react-query';
import { ForecastResponse, postForecast } from '../../api/robotapi';
import { DeepPartial } from '../../utils/typings';
import { useDebounce } from '../../hooks/useDebounce';
import { RiskProfileResponse, useGetRiskProfiles } from '../../api/investment-robot/useGetRiskProfiles';
import { DateTime } from 'luxon';
import { GOAL } from './GoalIndicationContext';

/** All amounts are in cents. All dates are in number of years */
export const forecastPeriodicDepositIntervals = ['yearly', 'monthly'] as const;

export type ForecastParameters = {
    capitalGoal: {
        amount: number;
        inYears: number;
    };
    deposits: {
        periodic: {
            amount: number;
            interval: typeof forecastPeriodicDepositIntervals[number];
        };
        initial: {
            amount: number;
        };
    };
    risk: {
        reduction: {
            isEnabled: boolean;
        };
        profile: {
            id: string;
            label: string;
        };
    };
    display: 'bar' | 'line';
};

export type Forecast = {
    parameters: ForecastParameters;
    query: UseQueryResult<ForecastResponse, unknown>;
    actions: {
        setParameters: (payload: DeepPartial<ForecastParameters>) => void;
    };
}

const getForecastContextDefaultValue = (searchParams?: URLSearchParams): ForecastParameters => {
    return {
        capitalGoal: {
            amount: searchParams?.get('capitalGoalAmount') ? +searchParams.get('capitalGoalAmount')! : 2000000,
            inYears: searchParams?.get('capitalGoalInYears') ? +searchParams.get('capitalGoalInYears')! : 15,
        },
        deposits: {
            initial: {
                amount: searchParams?.get('depositsInitialAmount')
                    ? +searchParams.get('depositsInitialAmount')!
                    : 1000000,
            },
            periodic: {
                amount: searchParams?.get('depositsPeriodicAmount')
                    ? +searchParams.get('depositsPeriodicAmount')!
                    : 20000,
                interval: searchParams?.get('depositsPeriodicInterval') === 'yearly' ? 'yearly' : 'monthly',
            },
        },
        risk: {
            profile: {
                id: searchParams?.get('riskProfileId') ? searchParams.get('riskProfileId')! : 'non-existent',
                label: searchParams?.get('riskProfileLabel') ? searchParams.get('riskProfileLabel')! : 'geen',
            },
            reduction: {
                isEnabled: searchParams?.get('riskReductionIsEnabled') === 'false' ? false : true,
            },
        },
        display: searchParams?.get('display') === 'line' ? 'line' : 'bar',
    };
};

export const getForecastContextFromSearchParams = (searchParams: URLSearchParams) => {
    return getForecastContextDefaultValue(searchParams);
};

export const getSearchParamsFromForecastParameters = (
    forecastParameters: ForecastParameters,
    selectedGoal?: GOAL,
): URLSearchParams => {
    const params = new URLSearchParams();
    params.set('capitalGoalAmount', forecastParameters.capitalGoal.amount + '');
    params.set('capitalGoalInYears', forecastParameters.capitalGoal.inYears + '');
    params.set('depositsInitialAmount', forecastParameters.deposits.initial.amount + '');
    params.set('depositsPeriodicAmount', forecastParameters.deposits.periodic.amount + '');
    params.set('depositsPeriodicInterval', forecastParameters.deposits.periodic.interval);
    params.set('riskProfileId', forecastParameters.risk.profile.id);
    params.set('riskProfileLabel', forecastParameters.risk.profile.label);
    params.set('riskReductionIsEnabled', forecastParameters.risk.reduction.isEnabled + '');
    params.set('display', forecastParameters.display);
    params.set('skipGoalIndication', 'true');
    if (selectedGoal) {
        params.set('selectedGoal', selectedGoal);
    }
    return params;
};

type ForecastParametersReducerAction = { type: 'MERGE'; payload: DeepPartial<ForecastParameters> };

const forecastParametersReducer: React.Reducer<
    ForecastParameters,
    { type: 'MERGE'; payload: DeepPartial<ForecastParameters> }
> = (currentParameters: ForecastParameters, action: ForecastParametersReducerAction) => {
    switch (action.type) {
        case 'MERGE':
            return merge({}, currentParameters, action.payload);
    }
};

export const ForecastContext = createContext<{
    forecast: Forecast,
    riskProfiles: {
        query: UseQueryResult<RiskProfileResponse, unknown>;
    };
}>({
    forecast: {
        parameters: getForecastContextDefaultValue(new URLSearchParams(window.location.search)),
        query: undefined!,
        actions: {
            setParameters: undefined!,
        },
    },
    riskProfiles: {
        query: undefined!,
    },
});

export const WithForecastContext: React.FC<{}> = ({ children }) => {
    // const urlParams = new URLSearchParams(window.location.search);
    // const myParam = urlParams.get('myParam');
    const [forecastParameters, setForecastParameters] = useReducer(
        forecastParametersReducer,
        getForecastContextDefaultValue(new URLSearchParams(window.location.search)),
    );

    const debouncedForecastParameters = useDebounce(forecastParameters, 500);

    const { risk } = forecastParameters;

    const forecastQuery = useQuery<ForecastResponse>(
        [
            'forecast',
            {
                ...debouncedForecastParameters,
                risk,
                // capitalGoal: { inYears: debouncedForecastParameters.capitalGoal.inYears },
            },
        ],
        async () => postForecast(forecastParameters),
        { keepPreviousData: true, staleTime: Infinity, enabled: forecastParameters.risk.profile.id !== 'non-existent' },
    );

    const riskProfilesQuery = useGetRiskProfiles(
        forecastParameters.risk.reduction.isEnabled,
        DateTime.now().plus({ years: forecastParameters.capitalGoal.inYears }),
    );

    return (
        <ForecastContext.Provider
            value={{
                forecast: {
                    parameters: forecastParameters,
                    query: forecastQuery,
                    actions: {
                        setParameters: (payload) => setForecastParameters({ type: 'MERGE', payload }),
                    },
                },
                riskProfiles: {
                    query: riskProfilesQuery,
                },
            }}
        >
            {children}
        </ForecastContext.Provider>
    );
};

export const getTotalDeposit = (parameters: ForecastParameters): number => {
    if (parameters.deposits.periodic.interval === 'monthly') {
        return (
            parameters.deposits.periodic.amount * 12 * parameters.capitalGoal.inYears +
            parameters.deposits.initial.amount
        );
    }
    return parameters.deposits.periodic.amount * parameters.capitalGoal.inYears + parameters.deposits.initial.amount;
};
